import { Component } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';
import { EntityTypeEnum } from 'src/app/core/enums/entity-type-id-enum';
import { ActionIdEnum } from 'src/app/core/enums/list-layout/action-id-enum';
import { ButtonIdEnum } from 'src/app/core/enums/list-layout/button-id-enum';
import { SortByTypeEnum } from 'src/app/core/enums/list-layout/sort-type-enum';
import { InvoiceExtendedModel } from 'src/app/core/models/commerce/invoice-extended.model';
import { MyfileModel } from 'src/app/core/models/myfile/myfile.model';
import { ExportType } from 'src/app/core/models/reporting/export-config-model';
import { EmitSortModel } from 'src/app/core/models/table/emit-sort.model';
import { ListFilterModel } from 'src/app/core/models/table/list-filter.model';
import { ServiceData } from 'src/app/core/models/table/service-data.model';
import { TableActionsModel } from 'src/app/core/models/table/table-action.model';
import { TableConfigurationModel } from 'src/app/core/models/table/table-configuration.model';
import { SecurityOperations } from 'src/app/core/security/security-operations';
import { AuthMyfileService } from 'src/app/core/services/auth.myfile.service';
import { OpenModalService } from 'src/app/core/services/modal-services/open-modal.service';
import { PdfViewerModalService } from 'src/app/core/services/modal-services/pdf-viewer-modal.service';
import { TaskModalService } from 'src/app/core/services/modal-services/task-modal.service';
import { MyfileApiService } from 'src/app/core/services/myfile-api.service';
import { ToasterNotificationService } from 'src/app/core/services/toaster-notification.service';
import { ListLayoutExtendableComponent } from '../list-layout/list-layout-extendable/list-layout-extendable.component';

import { BillingListService } from 'src/app/core/services/list-services/billing-list.service';
import { BillingListConfig } from './billing-list.config';
import { AiApiService } from 'src/app/core/services/ai-api.service';
import { TableConfigService } from 'src/app/core/services/table-config.service';
import { GlobalComponent } from 'src/app/global-component';

@Component({
  selector: 'app-billing-list',
  templateUrl: './billing-list.component.html',
  styleUrls: ['./billing-list.component.scss']
})
export class BillingListComponent extends ListLayoutExtendableComponent {

  tableConfig: TableConfigurationModel = new TableConfigurationModel();
  defaultTableConfig: TableConfigurationModel = new BillingListConfig();
  
  serviceData: ServiceData = new ServiceData();

  override listItemsObservable!: Observable<MyfileModel[]>;

  configItemsObservable!: Observable<TableConfigurationModel[]>;
  //Security Operations of the current User
  securityPermissionInvoiceUpdate: boolean = this.authMyFileService.canAccessOperation(SecurityOperations.requisition.requisitionCreateInvoice);

  protected override searchPlaceHolderText: string = "";

  constructor(
    protected taskModalService: TaskModalService,
    protected aiApiService: AiApiService,
    public billingListService: BillingListService,
    protected tableConfigService: TableConfigService,
    protected override myFileApiService: MyfileApiService,
    protected override sanitizer: DomSanitizer,
    protected override pdfModalService: PdfViewerModalService,
    protected override authMyFileService: AuthMyfileService,
    protected override openModalService: OpenModalService,
    protected override toasterNotificationService: ToasterNotificationService,
  ) {
    super(myFileApiService, sanitizer, pdfModalService, authMyFileService, toasterNotificationService, openModalService);
  }

  ngOnInit(): void {

    this.tableConfig = new BillingListConfig();

    this.configItemsObservable = this.tableConfigService.resultItems$;
    const subscrConfig = this.configItemsObservable.subscribe(result => {
      if (result && result[0]) {
        //needed to be able to avoid null from the model when its decoded. 
        this.tableConfig = JSON.parse(JSON.stringify(result[0], (key, value) => {
          if (value !== null) return value;
        }));
        if (this.tableConfig.pageSize !== GlobalComponent.defaultPagingInfo.pageSize) {
          this.billingListService.pageSize = this.tableConfig.pageSize;
        }
      }
    });
    this.unsubscribe.push(subscrConfig);

    this.tableConfigService.createdById = this.authMyFileService.currentUserContextValue?.id;
    this.tableConfigService.listTitle = this.tableConfig.listTitle!;

    this.listItemsObservable = this.billingListService.invoices$;
    this.total = this.billingListService.total$;
    this.isLoading = this.billingListService.loading$;

    const subscr = this.isLoading.subscribe(result => {
      if (result) {
        this.serviceData.loadingValue = true;
      } else {
        this.serviceData.loadingValue = false;
      }
    });
    this.unsubscribe.push(subscr);

    const subscr2 = this.listItemsObservable.subscribe(result => {
      this.listItems = Object.assign([], result);
      this.listItems.forEach((extendedInvoiceModel: InvoiceExtendedModel) => {
        this.getEntityPhoto(extendedInvoiceModel.account, EntityTypeEnum.Account);
        this.getEntityPhoto(extendedInvoiceModel.client, EntityTypeEnum.Client);
        this.getEntityPhoto(extendedInvoiceModel.requisition, EntityTypeEnum.Requisition);
        this.getEntityPhoto(extendedInvoiceModel.user, EntityTypeEnum.User);
        extendedInvoiceModel = this.addEntityType(extendedInvoiceModel, EntityTypeEnum.Invoice);
        extendedInvoiceModel.account = this.addEntityType(extendedInvoiceModel.account, EntityTypeEnum.Account);
        extendedInvoiceModel.client = this.addEntityType(extendedInvoiceModel.client, EntityTypeEnum.Client);
        extendedInvoiceModel.requisition = this.addEntityType(extendedInvoiceModel.requisition, EntityTypeEnum.Requisition);
        extendedInvoiceModel.user = this.addEntityType(extendedInvoiceModel.user, EntityTypeEnum.User);
      });
    });
    this.unsubscribe.push(subscr2);

    const subscr3 = this.total.subscribe(result => {
      this.serviceData.totalRecords = this.billingListService.totalRecords;
      this.serviceData.pageSize = this.billingListService.pageSize;
      this.serviceData.pageIndex = this.billingListService.pageIndex;
    });
    this.unsubscribe.push(subscr3);
  }

  ngOnDestroy() {
    this.listItems = undefined;
    this.clearSearch(this.billingListService);
    this.clearSearch(this.tableConfigService);
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

  addEntityType(model: any, entityType: EntityTypeEnum): any {
    if (model.entityType === undefined) {
      model.entityType = entityType;
    }
    return model;
  }

  //** new simpler sort function for further lists with new layout component */
  sortByPassedSort(passedSortModel: EmitSortModel) {
    if (passedSortModel.sortSelected == SortByTypeEnum.None) {
      this.serviceSort(this.billingListService, '', '');
    } else {
      this.serviceSort(this.billingListService, passedSortModel.fieldName, passedSortModel.sortSelected);
    }
  }

  override buttonActionSelected(buttonAction: any) {
    switch (buttonAction) {
      case `${ButtonIdEnum.ExportExcel}`:
        this.exportTo(ExportType.Excel, this.billingListService.searchModel, `${this.tableConfig.listTitle}`, "Invoices", this.tableConfig)
        break;
      case `${ButtonIdEnum.ExportCSV}`:
        this.exportTo(ExportType.Csv, this.billingListService.searchModel, `${this.tableConfig.listTitle}`, "Invoices", this.tableConfig)
        break;
    }
  }

  rowActionSelected(rowAction: TableActionsModel) {

    switch (rowAction.actionIdSelected) {
      case ActionIdEnum.View:
        this.viewMyfile(rowAction.model.requisitionId, rowAction.model.myFileId);
        break;
      case ActionIdEnum.Edit:
        this.openModel(rowAction.model.entityType, rowAction.model)
        break;
      case ActionIdEnum.Download:
        this.downloadMyfile(rowAction.model.requisitionId, rowAction.model.myFileId);
        break;
    }
  }

  filterSelected(filterSelected: ListFilterModel): void {
    switch (filterSelected.label) {
      case 1:
        this.billingListService.myfileStatus = filterSelected.selectedOption;
        break;
    }
  }

  updateListConfig(newListConfig: TableConfigurationModel) {
    //this.tableConfigService.listTitle = this.tableConfig.listTitle!;
  }

  downloadMyfile(requisitionId: string, myfileId: string,) {
    this.isLoadingVal = true;
    if (requisitionId && myfileId) {
      this.myFileApiService.downloadMyFile(requisitionId, myfileId).subscribe((result: any) => {
        this.isLoadingVal = false
      });
    }
  }

  openModel(entityType: EntityTypeEnum, model: any) {
    switch (entityType) {
      case EntityTypeEnum.Client:
        this.openModalService.editClientModal(this.billingListService, model.id);
        break;

      case EntityTypeEnum.Account:
        this.openModalService.editContactModal(this.billingListService, model.id);
        break;

      case EntityTypeEnum.Requisition:
        this.openModalService.editRequisitionModal(this.billingListService, model.id);
        break;

      case EntityTypeEnum.User:
        if (this.authMyFileService.canAccessOperation(SecurityOperations.user.userReadAll)) {
          this.openModalService.editUserModal(model.id, undefined, this.billingListService);
        }
        break;

      case EntityTypeEnum.Invoice:
        this.viewMyfile(model.requisitionId, model.myFileId);
        break;
    }
  }

  viewMyfile(requisitionId: string, myfileId: string) {
    this.isLoadingVal = true;
    if (requisitionId && myfileId) {
      this.myFileApiService.viewMyFile(requisitionId, myfileId).subscribe((result: any) => {
        if (result) {
          this.pdfModalService.openModal(result, result.name);
        }
        this.isLoadingVal = false;
        var sub: Subscription = this.pdfModalService.modelChanged.subscribe(() => {
          sub.unsubscribe();
        })
      });
    } else {
      this.isLoadingVal = false;
    }
  }
}
